import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export const PaymentVerifyPage = ({
  confirmOrder,
  isConfirmed,
  confirmedOrder,
  isLoading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMsg, setErrorMsg] = useState(null);
  const [attempted, setAttempted] = useState(false);

  const handleRetry = () => {
    const query = new URLSearchParams(location.search);
    const dbOrderId = query.get("id");
    const cashfreeOrderId = query.get("order_id");

    if (dbOrderId && cashfreeOrderId) {
      setErrorMsg(null);
      confirmOrder({
        id: parseInt(dbOrderId),
        paymentId: cashfreeOrderId,
        sessionId: localStorage.getItem("bhabhuti-session"),
      });
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const dbOrderId = query.get("id");
    const cashfreeOrderId = query.get("order_id");

    if (!dbOrderId || !cashfreeOrderId) {
      setErrorMsg("Invalid payment response parameters. Please try again.");
      return;
    }

    if (!attempted) {
      setAttempted(true);
      console.log(`Sending payment verification request: db_id=${dbOrderId}, cashfree_order_id=${cashfreeOrderId}`);
      confirmOrder({
        id: parseInt(dbOrderId),
        paymentId: cashfreeOrderId,
        sessionId: localStorage.getItem("bhabhuti-session"),
      });
    }
  }, [location, confirmOrder, attempted]);

  // Timeout fallback if verification takes too long
  useEffect(() => {
    let timer;
    if (attempted && !isConfirmed && !errorMsg) {
      timer = setTimeout(() => {
        if (!isConfirmed) {
          setErrorMsg("Payment verification is taking longer than expected. If money was deducted, your order will automatically update in your profile.");
        }
      }, 12000);
    }
    return () => clearTimeout(timer);
  }, [attempted, isConfirmed, errorMsg]);

  useEffect(() => {
    if (isConfirmed && confirmedOrder?.id) {
      console.log("Payment confirmed successfully! Redirecting to success page...");
      localStorage.removeItem("affiliateRefId");
      localStorage.removeItem("autoCouponCode");
      navigate(`/order-success/${confirmedOrder.id}`);
    }
  }, [isConfirmed, confirmedOrder, navigate]);

  return (
    <div className="verify-container">
      <div className="verify-card">
        {!errorMsg && (
          <div className="loader-section">
            <div className="pulse-loader"></div>
            <h2>Verifying Payment</h2>
            <p>Please wait while we verify your transaction status with Cashfree. Do not reload or close this page.</p>
          </div>
        )}

        {errorMsg && (
          <div className="error-section">
            <div className="error-icon">
              <i className="fa-solid fa-circle-xmark"></i>
            </div>
            <h2>Verification Failed</h2>
            <p className="error-text">{errorMsg}</p>
            <div className="button-group flex-wrap">
              <button className="btn btn-primary" onClick={handleRetry}>
                Retry Verification
              </button>
              <button className="btn btn-secondary" onClick={() => navigate("/orders")}>
                My Orders
              </button>
              <button className="btn btn-secondary" onClick={() => navigate("/")}>
                Home
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .verify-container {
          min-height: 70vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 20px;
          font-family: 'Outfit', 'Inter', sans-serif;
        }

        .verify-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          padding: 40px 30px;
          border-radius: 24px;
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.08);
          max-width: 480px;
          width: 100%;
          text-align: center;
          transition: all 0.3s ease;
        }

        .loader-section h2, .error-section h2 {
          color: #1a331e;
          font-weight: 700;
          margin-top: 25px;
          margin-bottom: 12px;
          font-size: 1.6rem;
        }

        .loader-section p, .error-section p {
          color: #4a5568;
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 0;
        }

        .pulse-loader {
          width: 80px;
          height: 80px;
          margin: 0 auto;
          background: radial-gradient(circle, #2e7d32 0%, #1b5e20 100%);
          border-radius: 50%;
          position: relative;
          box-shadow: 0 0 20px rgba(46, 125, 50, 0.4);
          animation: pulse 1.8s infinite ease-in-out;
        }

        .pulse-loader::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          background: transparent;
          border: 2px solid #2e7d32;
          border-radius: 50%;
          animation: ping 1.8s infinite ease-in-out;
        }

        .error-icon {
          font-size: 72px;
          color: #d32f2f;
          animation: shake 0.5s ease-in-out;
        }

        .error-text {
          background: rgba(211, 47, 47, 0.06);
          border-radius: 12px;
          padding: 12px 18px;
          color: #c62828 !important;
          border: 1px dashed rgba(211, 47, 47, 0.2);
          margin-top: 15px;
          margin-bottom: 25px !important;
        }

        .button-group {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .btn {
          padding: 10px 24px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: 600;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: #2e7d32;
          color: white;
          box-shadow: 0 4px 12px rgba(46, 125, 50, 0.2);
        }

        .btn-primary:hover {
          background: #1b5e20;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(46, 125, 50, 0.3);
        }

        .btn-secondary {
          background: #e2e8f0;
          color: #4a5568;
        }

        .btn-secondary:hover {
          background: #cbd5e1;
          color: #334155;
          transform: translateY(-2px);
        }

        @keyframes pulse {
          0% { transform: scale(0.9); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; box-shadow: 0 0 30px rgba(46, 125, 50, 0.6); }
          100% { transform: scale(0.9); opacity: 0.8; }
        }

        @keyframes ping {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.6); opacity: 0; }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
};
